import React from "react"
import { ReportedHourViewModel } from "../../api/reported-hours"
import HourReportTable from "./HourReportTable"
import HourReportEditForm from "./HourReportEditForm"
import { useHourReportMutations } from "../../hooks/use-hour-report-mutations"
import { useConfirmDialog } from "../../../components/ConfirmDialog"
import type { HourReportingWorkflowSettings } from "../../hooks/use-hour-reporting-workflow-settings"
import { useTranslation } from "react-i18next"

type HourReportListProps = {
  reports: ReportedHourViewModel[]
  totalReportedHours: { hours: number; minutes: number }
  reportedForName: string
  workflowSettings?: HourReportingWorkflowSettings
}

const HourReportList = ({ reports, totalReportedHours, reportedForName, workflowSettings }: HourReportListProps) => {
  const [editingReportId, setEditingReportId] = React.useState<string | null>(null)
  const { t } = useTranslation("hour-report")
  const editingReport = reports.find((report) => report.id === editingReportId)
  const { deleteHourReport, updateHourReportInfo, performHourReportAdminAction } = useHourReportMutations()

  const handleReadyForCheckChange = (report: ReportedHourViewModel, checked: boolean) => {
    if (report.editableAs === "hour-report-admin") {
      return performHourReportAdminAction(report.id, {
        action: checked ? "submit" : "unsubmit",
      })
    }

    return updateHourReportInfo(report.id, { status: checked ? "submitted" : "open" })
  }

  const { confirm: handleDeleteHourReport } = useConfirmDialog({
    title: t("dialogs.deleteTitle", { defaultValue: "Delete Hour Report" }),
    text: t("dialogs.deleteText", { defaultValue: "Are you sure you want to delete this hour report?" }),
    primaryActionButtonLabel: t("actions.delete", { defaultValue: "Delete" }),
    onConfirm: (id) => {
      const report = reports.find((report) => report.id === id)
      if (!report?.editableAs) return
      return deleteHourReport(id, report.editableAs)
    },
  })

  return (
    <>
      <HourReportTable
        reports={reports}
        totalReportedHours={totalReportedHours}
        reportedForName={reportedForName}
        setEditingReportId={setEditingReportId}
        onReadyForCheckChange={handleReadyForCheckChange}
        onDeleteHourReport={handleDeleteHourReport}
        supportsSubmission={workflowSettings?.supportsSubmission}
        supportsChecking={workflowSettings?.supportsChecking}
        supportsApproval={workflowSettings?.supportsApproval}
      />
      <HourReportEditForm
        report={editingReport}
        setEditingReportId={setEditingReportId}
        supportsSubmission={workflowSettings?.supportsSubmission}
      />
    </>
  )
}

export default HourReportList
